import { TextButtonLink } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { localize } from 'common/utils';
import { BootstrapNotificationToast } from 'modules/shared-menu/components/BootstrapNotificationToast';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useParallelBootstrapStatusPolling } from 'modules/shared-menu/hooks/useParallelBootstrapStatusPolling';
import { useSelectionList } from 'modules/shared-menu/hooks/useSelectionList';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from 'modules/shared-menu/types';
import { isSharedMenuBootstrapping } from 'modules/shared-menu/utils';

import SelectionListRow from './SelectionListRow';
import { SelectionListSortingIcon } from './SelectionListSortingIcon';
import { SelectionListToolbar } from './SelectionListToolbar';

type SelectionListProps = {
  country: Country;
  onEditSelectionVenues: (selection: SharedMenu) => void;
  selections: SharedMenu[];
  venuesWithSelectionLinks: VenueWithSharedMenuLinkInfo[];
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

const SelectionTable = styled.table`
  width: 100%;
  height: 100%;
  position: relative;
  border-collapse: collapse;
`;

const TableHeader = styled.tr`
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  height: 56px;
  vertical-align: middle;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  background: ${cbSemanticCssVars.surfaceMain};
`;

const TableHeaderCell = styled.th`
  ${typographyCss.Subtitle}
  vertical-align: middle;
  padding: 0 24px;
`;

const CellContainer = styled.td`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EmptyMessage = styled(Empty)`
  margin: 48px 16px;
`;

const StyledTextButtonLink = styled(TextButtonLink).attrs({ variant: 'black', iconPosition: 'right' })`
  ${typographyCss.Subtitle}
  white-space: nowrap;
`;

export const SelectionList = ({
  country,
  onEditSelectionVenues,
  selections,
  venuesWithSelectionLinks,
}: SelectionListProps) => {
  const { url } = useRouteMatch();
  const scope = useScopeContext();

  const { shownSelections, searchInput, searchSelections, appliedSortBy, handleSorting } = useSelectionList({
    selections,
    venuesWithSelectionLinks,
  });

  const handleResolvedBootstrappingUserNotification = useCallback(
    (status: 'success' | 'failure', sharedMenu: SharedMenu) => {
      const sharedMenuName = localize(sharedMenu.name, sharedMenu.primary_language);
      if (status === 'success') {
        toast.success(
          <BootstrapNotificationToast
            link={`${url}/${sharedMenu.id}/menu-editor`}
            sharedMenuName={sharedMenuName}
            status={status}
          />,
          {
            duration: 10000,
          },
        );
      } else {
        toast.error(
          <BootstrapNotificationToast
            link={`${url}/${sharedMenu.id}/menu-editor`}
            sharedMenuName={sharedMenuName}
            status={status}
          />,
          {
            duration: Infinity,
          },
        );
      }
    },
    [url],
  );

  const sharedMenusWithInProgressBootstrapping = useMemo(() => {
    return (selections ?? []).filter(selection => isSharedMenuBootstrapping(selection.draft));
  }, [selections]);

  useParallelBootstrapStatusPolling({
    enabled: true,
    scope,
    selections: sharedMenusWithInProgressBootstrapping,
    onResolve: handleResolvedBootstrappingUserNotification,
  });

  return (
    <Root>
      <SelectionListToolbar onSearch={searchSelections} searchInput={searchInput} />

      {shownSelections.length === 0 && <EmptyMessage title={'No menus'} />}

      {shownSelections.length > 0 && (
        <SelectionTable>
          <TableHeader>
            <TableHeaderCell>
              <CellContainer>
                <StyledTextButtonLink
                  icon={<SelectionListSortingIcon columnName="name" appliedSortBy={appliedSortBy} />}
                  onClick={() => handleSorting('name')}
                >
                  <FormattedMessage id="listing-manager.selections.table-columns.name" />
                </StyledTextButtonLink>
              </CellContainer>
            </TableHeaderCell>
            <TableHeaderCell>
              <CellContainer>
                <StyledTextButtonLink
                  icon={<SelectionListSortingIcon columnName="status" appliedSortBy={appliedSortBy} />}
                  onClick={() => handleSorting('status')}
                >
                  <FormattedMessage id="listing-manager.selections.table-columns.status" />
                </StyledTextButtonLink>
              </CellContainer>
            </TableHeaderCell>
            <TableHeaderCell>
              <CellContainer>
                <StyledTextButtonLink
                  icon={<SelectionListSortingIcon columnName="last-published" appliedSortBy={appliedSortBy} />}
                  onClick={() => handleSorting('last-published')}
                >
                  <FormattedMessage id="listing-manager.selections.table-columns.last-published" />
                </StyledTextButtonLink>
              </CellContainer>
            </TableHeaderCell>

            <TableHeaderCell>
              <CellContainer>
                <StyledTextButtonLink
                  icon={<SelectionListSortingIcon columnName="venue-count" appliedSortBy={appliedSortBy} />}
                  onClick={() => handleSorting('venue-count')}
                >
                  <FormattedMessage id="listing-manager.selections.table-columns.venues" />
                </StyledTextButtonLink>
              </CellContainer>
            </TableHeaderCell>
            <TableHeaderCell>
              <CellContainer>
                <FormattedMessage id="listing-manager.selections.table-columns.actions" />
              </CellContainer>
            </TableHeaderCell>
          </TableHeader>
          {shownSelections.map((selection, index) => (
            <SelectionListRow
              key={selection.id}
              country={country}
              onEditSelectionVenues={onEditSelectionVenues}
              selection={selection}
              index={index}
            />
          ))}
        </SelectionTable>
      )}
    </Root>
  );
};
